/**
 * AI Models — Quill's xAI/Anthropic model catalog, plus the RETIRED tier-default
 * record.
 *
 * ── WHAT CHANGED, AND WHY (2026-08-03 pipeline-model audit, finding 3) ────────
 * This page used to let an admin set a model for two "tier slots" (text /
 * text_fast) and roll those choices back. Migration 528 removed the tier-default
 * layer from model resolution: quill-engine's ModelResolver.resolvePipeline goes
 * straight to each pipeline row's ACTIVE model, and `resolve(slot)` — the only
 * reader of quill_model_defaults inside the engine — had zero call sites and has
 * since been deleted.
 *
 * So the editors on this page controlled nothing. PUT /models/defaults wrote the
 * row, pushed a cache invalidation to the engine, and returned success; no member
 * request had read that value since 528. A production admin surface that reports
 * "saved" for a change with no effect is the exact failure this codebase treats as
 * non-negotiable, so the editors are gone and the backend answers 410 GONE.
 *
 * What REMAINS, because it is real:
 *  • the model catalog + "Refresh from xAI" — live, and the Pipeline Models page
 *    depends on it;
 *  • the last-set slot values and the full change log — a genuine audit trail of
 *    what was set and by whom, now rendered strictly read-only.
 *
 * Models are set per pipeline, on the Pipeline Models page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Grid, Snackbar, Alert, Paper, Button, Skeleton, AlertTitle } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { formatDistanceToNow } from 'date-fns';
import { useQuillHealth } from '../QuillHealthContext';
import {
  quillApi,
  ModelDefaultsMap,
  ModelCatalogRow,
  ModelChangeLogRow,
  ModelSlot,
  ModelRefreshResponse,
} from '../../../services/quillAdminService';
import { colors, typography } from '../../../theme/designTokens';
import { SLOT_LABELS } from './slots';
import ModelCatalogTable from './ModelCatalogTable';
import ModelHistoryPanel from './ModelHistoryPanel';

const SLOTS: ModelSlot[] = ['text', 'text_fast'];

const RETIRED_NOTE =
  'Tier defaults were retired by migration 528 — models are set per pipeline.';

type Severity = 'success' | 'info' | 'error' | 'warning';

const AIModelsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Retired slot record (read-only) ─────────────────────────────────
  const [defaults, setDefaults] = useState<ModelDefaultsMap | null>(null);
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [defaultsError, setDefaultsError] = useState<string | null>(null);

  const loadDefaults = useCallback(async () => {
    setDefaultsLoading(true);
    setDefaultsError(null);
    try {
      const data = await quillApi.models.getCurrent();
      if (mountedRef.current) setDefaults(data);
    } catch (e) {
      if (mountedRef.current) {
        setDefaultsError(e instanceof Error ? e.message : 'Failed to load the retired slot record.');
      }
    } finally {
      if (mountedRef.current) setDefaultsLoading(false);
    }
  }, []);

  // ── Catalog ─────────────────────────────────────────────────────────
  const [catalog, setCatalog] = useState<ModelCatalogRow[]>([]);
  const [catalogMeta, setCatalogMeta] = useState<{
    last_synced_at: string | null;
    stale: boolean;
    sync_age_hours: number | null;
  }>({ last_synced_at: null, stale: false, sync_age_hours: null });
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const resp = await quillApi.models.getAvailable();
      if (mountedRef.current) {
        setCatalog(resp.data);
        setCatalogMeta({
          last_synced_at: resp.last_synced_at,
          stale: resp.stale,
          sync_age_hours: resp.sync_age_hours,
        });
      }
    } catch (e) {
      if (mountedRef.current) {
        setCatalogError(e instanceof Error ? e.message : 'Failed to load the model catalog.');
      }
    } finally {
      if (mountedRef.current) setCatalogLoading(false);
    }
  }, []);

  // ── History (all slots, filtered per-slot client-side) ────────────────
  const [history, setHistory] = useState<ModelChangeLogRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const rows = await quillApi.models.getHistory({ limit: 200 });
      if (mountedRef.current) setHistory(rows);
    } catch (e) {
      if (mountedRef.current) {
        setHistoryError(e instanceof Error ? e.message : 'Failed to load change history.');
      }
    } finally {
      if (mountedRef.current) setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    // Each section resolves independently — a rejection in one does not
    // block or blank the others.
    Promise.allSettled([loadDefaults(), loadCatalog(), loadHistory()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Snackbar ────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState<{ message: string; severity: Severity } | null>(null);
  const notify = useCallback((message: string, severity: Severity = 'success') => {
    setSnackbar({ message, severity });
  }, []);

  // ── History drawer ──────────────────────────────────────────────────
  const [historySlot, setHistorySlot] = useState<ModelSlot | null>(null);

  const handleCatalogRefreshed = useCallback((resp: ModelRefreshResponse) => {
    setCatalog(resp.data);
    setCatalogMeta({
      last_synced_at: resp.last_synced_at,
      stale: resp.stale,
      sync_age_hours: resp.sync_age_hours,
    });
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header — 48px teal icon tile + title + subtitle */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <SmartToyIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
            AI Models
          </Typography>
          <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
            Quill's xAI and Anthropic model catalog, and the retired tier-slot record.
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Local backend • {status === 'up' ? 'connected' : status}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>{RETIRED_NOTE}</AlertTitle>
        Migration 528 removed the tier-default layer from model resolution, and the
        engine no longer reads these slots for anything. The editors that used to
        live here have been removed because saving them changed nothing — the API
        now answers 410 Gone. Set a model on the <strong>Pipeline Models</strong>{' '}
        page. The values and history below are kept as the record of what was set
        before 528.
      </Alert>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {SLOTS.map((slot) => (
          <Grid item xs={12} md={6} key={slot}>
            <Paper
              variant="outlined"
              sx={{ p: 3, borderRadius: '16px', borderColor: colors.gray[200], height: '100%' }}
              data-testid={`retired-slot-${slot}`}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
                  {SLOT_LABELS[slot]}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Retired
                </Typography>
              </Box>

              {defaultsLoading && (
                <Box>
                  <Skeleton variant="text" width="70%" height={28} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              )}

              {!defaultsLoading && defaultsError && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={loadDefaults}>
                      Retry
                    </Button>
                  }
                >
                  {defaultsError}
                </Alert>
              )}

              {!defaultsLoading && !defaultsError && (
                <>
                  <Typography
                    sx={{
                      fontFamily: typography.fonts.mono,
                      fontSize: typography.sizes.base,
                      wordBreak: 'break-all',
                      color: colors.gray[800],
                      mb: 1,
                    }}
                  >
                    {defaults?.[slot]?.value ?? '—'}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {defaults?.[slot]?.updated_by
                      ? `Last set by ${defaults[slot]!.updated_by}${
                          defaults[slot]!.updated_at
                            ? ` • ${formatDistanceToNow(new Date(defaults[slot]!.updated_at as string), { addSuffix: true })}`
                            : ''
                        }`
                      : 'No change recorded'}
                  </Typography>

                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    This value is not read by the engine. It is kept for the audit trail.
                  </Typography>

                  <Button variant="text" size="small" onClick={() => setHistorySlot(slot)} sx={{ textTransform: 'none' }}>
                    History
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <ModelCatalogTable
        catalog={catalog}
        loading={catalogLoading}
        error={catalogError}
        onRetry={loadCatalog}
        meta={catalogMeta}
        onRefreshed={handleCatalogRefreshed}
        notify={notify}
      />

      <ModelHistoryPanel
        open={historySlot !== null}
        slot={historySlot}
        rows={historySlot ? history.filter((h) => h.slot === historySlot) : []}
        loading={historyLoading}
        error={historyError}
        onRetry={loadHistory}
        onClose={() => setHistorySlot(null)}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AIModelsPage;
